import { extendTheme } from '@chakra-ui/react'
import images from './images'

/**
 * Baotree Design System — Nature-inspired with warm accents.
 *
 * Color Palette:
 *  • #1b4543 — Dark teal (primary dark, headings, nav, footers)
 *  • #008c36 — Green (primary brand, buttons, links, active states)
 *  • #02cb56 — Bright green (accents, success states, highlights)
 *  • #ffc369 — Golden amber (secondary accent, badges, notifications)
 *  • #e9e9e9 — Light gray (borders, dividers, subtle backgrounds)
 *  • #fefefe — Near white (page backgrounds, cards)
 *
 * Principles:
 *  • Zero border-radius everywhere (sharp, architectural geometry)
 *  • Green-teal brand palette — dark teal headings, green primary actions
 *  • Uppercase micro-labels: xs/10px, letterSpacing 0.04–0.08em, fontWeight 500–600
 *  • Light headings: fontWeight 300–400, letterSpacing -0.01em to -0.02em
 *  • Subtle entrance animations: translateY(20px)→0, staggered
 *  • Blur backdrops on overlays
 *  • Poppins font throughout
 */

const borderRadius = '0'
const transition = 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'

const theme = extendTheme({
  // ─── Fonts ───────────────────────────────────────────────
  fonts: {
    body: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    heading: "'Poppins', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
    mono: "'JetBrains Mono', 'Fira Code', monospace"
  },

  // ─── Breakpoints (mobile-first) ─────────────────────────
  breakpoints: {
    sm: '30em', // 480px
    md: '48em', // 768px
    lg: '62em', // 992px
    xl: '80em', // 1280px
    '2xl': '96em' // 1536px
  },

  // ─── Colors ──────────────────────────────────────────────
  colors: {
    lightGreen: {
      100: 'rgba(0,140,54,0.15)'
    },
    gray: {
      50: '#fefefe',
      100: '#F5F5F5',
      200: '#e9e9e9',
      300: '#D4D4D4',
      400: '#737373',
      500: '#525252',
      600: '#404040',
      700: '#2f2f2f',
      800: '#262626',
      900: '#1b4543'
    },
    google: {
      50: '#FF5E57',
      100: '#FF5E57',
      200: '#FF5E57',
      300: '#FF5E57',
      400: '#FF5E57',
      500: '#FF5E57'
    },
    brand: {
      50: '#e6f7ec',
      100: '#b3e8c7',
      200: '#80d9a3',
      300: '#4dca7e',
      400: '#02cb56',
      // 4.96:1 with white text (was #008c36 at 4.37 — an AA fail at button
      // sizes). Visually near-identical; measurably legal.
      500: '#00822f',
      600: '#007a2f',
      700: '#006828',
      800: '#1b4543',
      900: '#0f302e'
    },
    accent: {
      50: '#FFF8EB',
      100: '#FFECC3',
      200: '#FFE0A0',
      300: '#FFD47D',
      400: '#ffc369',
      500: '#F5A623',
      600: '#D4901A',
      700: '#B37A14',
      800: '#8C5E0E',
      900: '#664308'
    },
    success: {
      50: '#e3fbee',
      100: '#c3ebd7',
      200: '#a0dcbf',
      300: '#7ccda7',
      400: '#59bf8e',
      500: '#40a674',
      600: '#30815a',
      700: '#205c40',
      800: '#0e3825',
      900: '#001509'
    },
    text: {
      50: '#737373',
      100: '#525252',
      200: '#404040',
      300: '#2f2f2f',
      400: '#262626',
      500: '#1b4543',
      700: '#0f302e',
      800: '#0a2120',
      900: '#050f0e'
    },
    background: {
      50: '#FFFFFF',
      100: '#fefefe',
      200: '#F5F5F5',
      300: '#F0F0F0',
      400: '#e9e9e9',
      500: '#D4D4D4',
      600: '#A3A3A3',
      700: '#737373',
      800: '#1b4543',
      900: '#0f302e'
    },
    primary: {
      50: '#e6f7ec',
      100: '#b3e8c7',
      200: '#80d9a3',
      300: '#4dca7e',
      400: '#02cb56',
      500: '#008c36',
      600: '#007a2f',
      700: '#006828',
      800: '#1b4543',
      900: '#0f302e'
    },
    secondary: {
      50: '#FFF8EB',
      100: '#FFECC3',
      200: '#FFE0A0',
      300: '#FFD47D',
      400: '#ffc369',
      500: '#F5A623',
      600: '#D4901A',
      700: '#B37A14',
      800: '#8C5E0E',
      900: '#664308'
    },
    info: {
      50: '#EFF6FF',
      100: '#DBEAFE',
      200: '#BFDBFE',
      300: '#93C5FD',
      400: '#60A5FA',
      500: '#3B82F6',
      600: '#2563EB',
      700: '#1D4ED8',
      800: '#1E40AF',
      900: '#1E3A8A'
    }
  },

  // ─── Shadows ─────────────────────────────────────────────
  shadows: {
    outline: 'none',
    xs: '0 1px 2px rgba(0,0,0,0.04)',
    sm: '0 2px 4px rgba(0,0,0,0.06)',
    md: '0 4px 12px rgba(0,0,0,0.08)',
    lg: '0 8px 24px rgba(0,0,0,0.10)',
    xl: '0 16px 48px rgba(0,0,0,0.12)',
    card: '0 1px 3px rgba(0,0,0,0.06)',
    'card-hover': '0 4px 16px rgba(0,0,0,0.10)',
    nav: '0 1px 0 rgba(0,0,0,0.06)'
  },

  // ─── Component Overrides ─────────────────────────────────
  components: {
    // ── Button ─────────────────────────────────────────────
    Button: {
      baseStyle: {
        fontWeight: '600',
        letterSpacing: '0.04em',
        borderRadius,
        textTransform: 'uppercase' as const,
        fontSize: 'xs',
        transition,
        // Mouse clicks stay clean; the keyboard MUST see where it is.
        // Suppressing _focusVisible made every button invisible to keyboard
        // users across the whole app (measured: :focus-visible true, computed
        // outline none).
        _focus: { boxShadow: 'none' },
        _focusVisible: { boxShadow: '0 0 0 3px rgba(2, 203, 86, 0.45)' }
      },
      sizes: {
        xs: { h: 7, px: 3, fontSize: '2xs' },
        sm: { h: 9, px: 4, fontSize: 'xs' },
        md: { h: 11, px: 6, fontSize: 'xs' },
        lg: { h: 12, px: 8, fontSize: 'sm' }
      },
      variants: {
        solid: (props: any) => ({
          bg: props.colorScheme === 'gray' ? 'brand.500' : undefined,
          color: props.colorScheme === 'gray' ? 'white' : undefined,
          _hover: {
            bg: props.colorScheme === 'gray' ? 'brand.600' : undefined,
            transform: 'translateY(-1px)',
            shadow: 'md'
          },
          _active: {
            bg: props.colorScheme === 'gray' ? 'brand.700' : undefined,
            transform: 'translateY(0)'
          }
        }),
        outline: {
          borderColor: 'gray.300',
          color: 'gray.900',
          bg: 'white',
          _hover: {
            bg: 'brand.50',
            borderColor: 'brand.500',
            color: 'brand.500',
            transform: 'translateY(-1px)',
            shadow: 'sm'
          },
          _active: {
            transform: 'translateY(0)'
          }
        },
        ghost: {
          color: 'gray.600',
          _hover: {
            bg: 'brand.50',
            color: 'brand.500'
          }
        },
        link: {
          color: 'brand.500',
          fontWeight: '500',
          textDecoration: 'none',
          _hover: {
            color: 'brand.700',
            textDecoration: 'none'
          }
        }
      },
      defaultProps: {
        colorScheme: 'gray'
      }
    },

    // ── Input ──────────────────────────────────────────────
    Input: {
      baseStyle: {
        field: {
          borderRadius,
          _placeholder: { color: 'gray.400', fontSize: 'sm' }
        }
      },
      variants: {
        outline: {
          field: {
            borderRadius,
            borderColor: 'gray.200',
            bg: 'white',
            _focus: {
              borderColor: 'brand.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
            },
            _hover: {
              borderColor: 'gray.400'
            }
          }
        },
        filled: {
          field: {
            borderRadius,
            bg: 'gray.100',
            borderWidth: '1px',
            borderColor: 'transparent',
            _focus: {
              bg: 'white',
              borderColor: 'brand.500',
              boxShadow: 'none'
            },
            _hover: {
              bg: 'gray.200'
            }
          }
        },
        unstyled: {
          field: {
            borderRadius,
            px: 0
          }
        }
      },
      defaultProps: {
        focusBorderColor: 'brand.500'
      }
    },

    // ── Textarea ───────────────────────────────────────────
    Textarea: {
      baseStyle: {
        borderRadius,
        _placeholder: { color: 'gray.400', fontSize: 'sm' }
      },
      variants: {
        outline: {
          borderRadius,
          borderColor: 'gray.200',
          _focus: {
            borderColor: 'brand.500',
            boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
          },
          _hover: {
            borderColor: 'gray.400'
          }
        },
        filled: {
          borderRadius,
          bg: 'gray.100',
          _focus: {
            bg: 'white',
            borderColor: 'brand.500'
          }
        }
      },
      defaultProps: {
        focusBorderColor: 'brand.500'
      }
    },

    // ── Select ─────────────────────────────────────────────
    Select: {
      baseStyle: {
        field: {
          borderRadius
        }
      },
      variants: {
        outline: {
          field: {
            borderRadius,
            borderColor: 'gray.200',
            _focus: {
              borderColor: 'brand.500',
              boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)'
            }
          }
        },
        filled: {
          field: {
            borderRadius,
            bg: 'gray.100',
            _focus: {
              bg: 'white',
              borderColor: 'brand.500'
            }
          }
        }
      },
      defaultProps: {
        focusBorderColor: 'brand.500'
      }
    },

    // ── Heading ────────────────────────────────────────────
    Heading: {
      baseStyle: {
        fontWeight: '400',
        letterSpacing: '-0.02em',
        color: 'gray.900'
      },
      sizes: {
        '2xl': { fontWeight: '300', letterSpacing: '-0.03em' },
        xl: { fontWeight: '300', letterSpacing: '-0.02em' },
        lg: { fontWeight: '400', letterSpacing: '-0.02em' },
        md: { fontWeight: '500', letterSpacing: '-0.01em' },
        sm: { fontWeight: '600', letterSpacing: '0' }
      }
    },

    // ── Text ───────────────────────────────────────────────
    Text: {
      baseStyle: {
        color: 'gray.600',
        lineHeight: '1.7'
      }
    },

    // ── FormLabel ──────────────────────────────────────────
    FormLabel: {
      baseStyle: {
        fontSize: 'xs',
        fontWeight: '500',
        color: 'gray.600',
        textTransform: 'uppercase' as const,
        letterSpacing: '0.04em',
        mb: 1
      }
    },

    // ── Badge ──────────────────────────────────────────────
    Badge: {
      baseStyle: {
        borderRadius,
        textTransform: 'uppercase' as const,
        fontSize: '2xs',
        fontWeight: '600',
        letterSpacing: '0.06em',
        px: 2,
        py: 0.5
      }
    },

    // ── Tag ────────────────────────────────────────────────
    Tag: {
      baseStyle: {
        container: {
          borderRadius,
          fontWeight: '500',
          letterSpacing: '0.02em'
        }
      }
    },

    // ── Card ───────────────────────────────────────────────
    Card: {
      baseStyle: {
        container: {
          borderRadius,
          border: '1px solid',
          borderColor: 'gray.200',
          bg: 'white',
          shadow: 'none',
          transition,
          _hover: {
            shadow: 'card-hover'
          }
        }
      }
    },

    // ── Modal ──────────────────────────────────────────────
    Modal: {
      baseStyle: {
        overlay: {
          bg: 'blackAlpha.600',
          backdropFilter: 'blur(8px)'
        },
        dialog: {
          borderRadius,
          bg: 'white',
          shadow: 'xl'
        },
        header: {
          fontWeight: '400',
          letterSpacing: '-0.02em',
          fontSize: 'lg',
          borderBottom: '1px solid',
          borderColor: 'gray.200',
          pb: 4
        },
        body: {
          py: 6
        },
        footer: {
          borderTop: '1px solid',
          borderColor: 'gray.200',
          pt: 4
        },
        closeButton: {
          borderRadius,
          _focus: { boxShadow: 'none' }
        }
      }
    },

    // ── Drawer ─────────────────────────────────────────────
    Drawer: {
      baseStyle: {
        overlay: {
          bg: 'blackAlpha.500',
          backdropFilter: 'blur(4px)'
        },
        dialog: {
          bg: 'white',
          shadow: 'xl'
        },
        header: {
          fontWeight: '400',
          borderBottom: '1px solid',
          borderColor: 'gray.200'
        }
      }
    },

    // ── Tabs ───────────────────────────────────────────────
    Tabs: {
      variants: {
        line: {
          tab: {
            fontWeight: '600',
            fontSize: 'xs',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.04em',
            color: 'gray.500',
            borderBottom: '2px solid transparent',
            transition,
            _selected: {
              color: 'brand.500',
              borderColor: 'brand.500'
            },
            _hover: {
              color: 'gray.900'
            },
            _focus: { boxShadow: 'none' }
          },
          tablist: {
            borderBottom: '1px solid',
            borderColor: 'gray.200'
          }
        },
        enclosed: {
          tab: {
            borderRadius,
            fontWeight: '500',
            fontSize: 'xs',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.04em',
            _selected: {
              bg: 'white',
              borderColor: 'gray.200',
              borderBottomColor: 'white',
              color: 'gray.900'
            }
          }
        }
      }
    },

    // ── Table ──────────────────────────────────────────────
    Table: {
      variants: {
        simple: {
          th: {
            textTransform: 'uppercase' as const,
            letterSpacing: '0.06em',
            fontWeight: '600',
            fontSize: '2xs',
            color: 'gray.500',
            borderColor: 'gray.200',
            bg: 'gray.50',
            py: 3
          },
          td: {
            fontSize: 'sm',
            borderColor: 'gray.100',
            py: 3
          },
          tbody: {
            tr: {
              transition,
              _hover: {
                bg: 'gray.50'
              }
            }
          }
        }
      }
    },

    // ── Menu ───────────────────────────────────────────────
    Menu: {
      baseStyle: {
        list: {
          borderRadius,
          border: '1px solid',
          borderColor: 'gray.200',
          shadow: 'lg',
          py: 1
        },
        item: {
          fontSize: 'sm',
          py: 2,
          px: 4,
          transition,
          _hover: {
            bg: 'gray.100'
          },
          _focus: {
            bg: 'gray.100'
          }
        }
      }
    },

    // ── Popover ────────────────────────────────────────────
    Popover: {
      baseStyle: {
        content: {
          borderRadius,
          border: '1px solid',
          borderColor: 'gray.200',
          shadow: 'lg',
          _focus: { boxShadow: 'lg' }
        }
      }
    },

    // ── Tooltip ────────────────────────────────────────────
    Tooltip: {
      baseStyle: {
        borderRadius,
        bg: 'gray.900',
        color: 'white',
        fontSize: 'xs',
        fontWeight: '500',
        px: 3,
        py: 1.5,
        letterSpacing: '0.02em'
      }
    },

    // ── Alert ──────────────────────────────────────────────
    Alert: {
      baseStyle: {
        container: {
          borderRadius,
          px: 4,
          py: 3
        }
      }
    },

    // ── Accordion ──────────────────────────────────────────
    Accordion: {
      baseStyle: {
        container: {
          borderColor: 'gray.200'
        },
        button: {
          _focus: { boxShadow: 'none' },
          _hover: { bg: 'gray.50' },
          py: 3,
          fontWeight: '500',
          fontSize: 'sm'
        },
        panel: {
          pt: 2,
          pb: 4
        }
      }
    },

    // ── Switch ─────────────────────────────────────────────
    Switch: {
      baseStyle: {
        track: {
          bg: 'gray.300',
          _checked: { bg: 'brand.500' }
        }
      }
    },

    // ── Checkbox ───────────────────────────────────────────
    Checkbox: {
      baseStyle: {
        control: {
          borderRadius,
          borderColor: 'gray.400',
          _checked: {
            bg: 'brand.500',
            borderColor: 'brand.500',
            _hover: {
              bg: 'brand.600',
              borderColor: 'brand.600'
            }
          },
          _focus: { boxShadow: 'none' }
        },
        label: {
          fontSize: 'sm'
        }
      }
    },

    // ── Radio ──────────────────────────────────────────────
    Radio: {
      baseStyle: {
        control: {
          borderColor: 'gray.400',
          _checked: {
            bg: 'brand.500',
            borderColor: 'brand.500',
            _hover: {
              bg: 'brand.600',
              borderColor: 'brand.600'
            }
          },
          _focus: { boxShadow: 'none' }
        }
      }
    },

    // ── Skeleton ───────────────────────────────────────────
    Skeleton: {
      baseStyle: {
        borderRadius
      }
    },

    // ── Divider ────────────────────────────────────────────
    Divider: {
      baseStyle: {
        borderColor: 'gray.200'
      }
    },

    // ── Avatar ─────────────────────────────────────────────
    Avatar: {
      baseStyle: {
        container: {
          borderRadius
        },
        excessLabel: {
          borderRadius,
          bg: 'gray.200',
          color: 'gray.700',
          fontWeight: '600'
        }
      }
    },

    // ── Spinner ────────────────────────────────────────────
    Spinner: {
      defaultProps: {
        color: 'gray.500'
      }
    },

    // ── Progress ───────────────────────────────────────────
    Progress: {
      baseStyle: {
        track: {
          borderRadius,
          bg: 'gray.200'
        },
        filledTrack: {
          borderRadius,
          bg: 'brand.500'
        }
      }
    },

    // ── Breadcrumb ─────────────────────────────────────────
    Breadcrumb: {
      baseStyle: {
        link: {
          fontSize: 'xs',
          fontWeight: '500',
          color: 'gray.500',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.04em',
          _hover: {
            color: 'brand.500',
            textDecoration: 'none'
          }
        },
        separator: {
          color: 'gray.400'
        }
      }
    }
  },

  // ─── Global Styles ───────────────────────────────────────
  styles: {
    global: {
      'html, body': {
        bg: 'background.100',
        color: 'text.500',
        WebkitFontSmoothing: 'antialiased',
        MozOsxFontSmoothing: 'grayscale'
      },
      '*::placeholder': {
        color: 'gray.400',
        fontSize: 'sm'
      },
      '*:focus': {
        boxShadow: 'none !important'
      },
      'a:focus, a:focus-visible': {
        outline: 'none'
      },
      /* Aurant-style scrollbar */
      '::-webkit-scrollbar': {
        width: '6px',
        height: '6px'
      },
      '::-webkit-scrollbar-track': {
        bg: 'transparent'
      },
      '::-webkit-scrollbar-thumb': {
        bg: 'gray.300',
        borderRadius: '3px',
        _hover: {
          bg: 'gray.400'
        }
      }
    }
  },

  // ─── Legacy compat ───────────────────────────────────────
  boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
  gridGutter: 1
})

export { theme, images }
